import React, { useEffect } from "react";
import { Container,
  List,
  ListItem,
  Typography
 } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { selectLeaderboardPlayers } from "./leaderboardSlice";
import { selectPlayer } from "../player/playerSlice";
import {
  selectPlayerSubmissionsForGameboardLeaderboard,
  stopWatchingQuestionSubmissionsAction,
  watchQuestionSubmissionsAction,
} from "../question/submissionSlice";
import { selectCurrentQuestionObject } from "../question/questionSlice";
import logging from "utils/logging";
import {
  selectGameId,
  selectGameStatusObject,
} from "../gamestate/gameStateSlice";
import {
  GAMESTATE_ROUND,
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
  GAMESTATE_ROUNDSTATE_QUESTION_REVEALING,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
} from "../gamestate/constants";

const log = logging.getLogger("Player Leaderboard");
log.setLevel('warn');

const Root = styled('div')({
  width: '100%',
});

const StyledContainer = styled(Container)({
  width: '100%',
});

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'theme',
})(({ theme, highlighted }) => ({
  backgroundColor: '#fff',
  color: '#222',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.1em",
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  border: highlighted ? `2px solid #2ec4b6` : '1px solid #e0e0e0',
  "& .MuiListItemText-primary": {
    fontFamily: "'Poppins', 'Inter', sans-serif",
    color: highlighted ? '#2ec4b6' : '#222',
    fontSize: "1.1em",
    fontWeight: highlighted ? 600 : 400,
  },
  "& .MuiListItemText-secondary": {
    color: '#666',
    fontSize: "0.9em",
  },
}));

const HeaderText = styled(Typography)({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "2em",
  textAlign: "center",
  color: '#2ec4b6',
  fontWeight: 700,
  marginBottom: '24px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  textShadow: '2px 2px 4px rgba(46, 196, 182, 0.3)',
  borderBottom: '3px solid #2ec4b6',
  paddingBottom: '8px',
});

const MoneyText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'negative',
})(({ highlighted, negative }) => ({
  color: highlighted ? '#2ec4b6' : negative ? '#e74c3c' : '#222',
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.3em",
  fontWeight: 600,
  textAlign: "center",
}));

const PlayerInfoContainer = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const PlayerNameRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PlayerName = styled('span')(({ highlighted }) => ({
  fontWeight: 600,
  color: highlighted ? '#2ec4b6' : '#222',
}));

const FastestStar = styled('span')({
  color: '#ffe066',
  fontSize: '1.2em',
});

const ResultsInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '0.9em',
});

const Divider = styled('span')({
  color: '#ccc',
  fontSize: '0.8em',
  fontWeight: 300,
});

const AnswerText = styled('span')({
  color: '#666',
});

const TimeText = styled('span')({
  color: '#666',
});

const PointsText = styled('span')(({ isCorrect }) => ({
  color: isCorrect ? '#2ec4b6' : '#e74c3c',
  fontWeight: 600,
}));

const AnswerOnlyText = styled('span')({
  color: '#666',
  fontSize: '0.9em',
});

const WagerStatus = styled('span')(({ wagered }) => ({
  fontSize: '0.85em',
  fontWeight: 500,
  color: wagered ? '#2ec4b6' : '#999',
  fontStyle: wagered ? 'normal' : 'italic',
}));

const PlayerLeaderboardRow = ({ player, highlighted, index, submission, isResultsMode, isWageringMode, fastestPlayerId, questionStartTime }) => {
  const formatTime = (submittedAt) => {
    if (!submittedAt || !questionStartTime) return null;
    const submitTime = new Date(submittedAt);
    const startTime = new Date(questionStartTime);
    const timeDiff = (submitTime - startTime) / 1000;
    return timeDiff.toFixed(2);
  };

  const getSubmissionInfo = () => {
    if (!submission || !isResultsMode) return null;
    
    const time = formatTime(submission.submittedAt);
    const isCorrect = submission.result === 'correct' || submission.status === 'correct';
    const isFastest = fastestPlayerId === player.id;
    
    return {
      time,
      isCorrect,
      isFastest,
      points: submission.points || 0
    };
  };

  const submissionInfo = getSubmissionInfo();

  return (
    <StyledListItem
      highlighted={highlighted}
      divider={true}
    >
      <PlayerInfoContainer>
        <PlayerNameRow>
          <PlayerName highlighted={highlighted}>
            {index + 1}. {player.name}
          </PlayerName>
          {submissionInfo?.isFastest && (
            <FastestStar>⭐</FastestStar>
          )}
        </PlayerNameRow>
        
        {isWageringMode ? (
          <WagerStatus wagered={!!submission}>
            {submission ? 'Wager locked in' : 'Deciding...'}
          </WagerStatus>
        ) : submissionInfo ? (
          <ResultsInfo>
            <AnswerText>
              {submission.answer || ''}
            </AnswerText>
            <Divider>•</Divider>
            <TimeText>
              {submissionInfo.time}s
            </TimeText>
            <Divider>•</Divider>
            <PointsText isCorrect={submissionInfo.isCorrect}>
              {submissionInfo.points >= 0 ? '+' : ''}{submissionInfo.points}
            </PointsText>
          </ResultsInfo>
        ) : (
          <AnswerOnlyText>
            {submission?.answer || ''}
          </AnswerOnlyText>
        )}
      </PlayerInfoContainer>
      
      <MoneyText
        variant="h5"
        highlighted={highlighted}
        negative={player.score < 0}
        align="right"
      >
        {player.score >= 0
          ? "$" + player.score.toLocaleString()
          : "- $" + Math.abs(player.score).toLocaleString()}
      </MoneyText>
    </StyledListItem>
  );
};

const PlayerLeaderboard = () => {
  const dispatch = useDispatch();

  const gameStateObj = useSelector(selectGameStatusObject);
  const gameId = useSelector(selectGameId);
  const questionId = gameStateObj.questionId;
  const players = useSelector(selectLeaderboardPlayers);
  const gameState = useSelector(selectGameStatusObject);
  const players2 = useSelector(selectPlayerSubmissionsForGameboardLeaderboard);
  const currentQuestion = useSelector(selectCurrentQuestionObject);

  const currentPlayer = useSelector(selectPlayer);

  useEffect(() => {
    if (gameId && questionId) {
      dispatch(watchQuestionSubmissionsAction(gameId, questionId));
    }

    return function cleanup() {
      if (gameId && questionId) {
        dispatch(stopWatchingQuestionSubmissionsAction(gameId, questionId));
      }
    };
  }, [dispatch, questionId, gameId]);

  let playerList = players;

  log.debug("Game State: ", gameState);
  log.debug("Players: ", players);
  log.debug("Players2: ", players2);
  log.debug("Player List: ", playerList);
  log.debug("Current Player: ", currentPlayer);

  const isWageringMode =
    gameState.currentState === GAMESTATE_ROUND &&
    gameState.round === GAMESTATE_ROUND_FINAL &&
    gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_WAGERING;

  if (
    isWageringMode ||
    (gameState.currentState === GAMESTATE_ROUND &&
      gameState.round !== GAMESTATE_ROUND_FINAL &&
      (gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_RESULTS ||
        gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_REVEALING))
  ) {
    playerList = players2;
  }

  // Determine if we're in results mode
  // For DD, skip judging state to avoid spoiling the result before narration
  // For Final round, never show results — the reveal sequence handles it on the gameboard
  const isResultsMode = gameState.currentState === GAMESTATE_ROUND &&
    gameState.round !== GAMESTATE_ROUND_FINAL &&
    (gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_RESULTS ||
     gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_REVEALING ||
     (gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_JUDGING && !gameState.dailyDouble));


  // Find the fastest correct answer
  let fastestPlayerId = null;
  if (isResultsMode && playerList) {
    const correctSubmissions = playerList
      .filter(player => player.submission && 
        (player.submission.result === 'correct' || player.submission.status === 'correct'))
      .sort((a, b) => {
        const timeA = a.submission.submittedAt ? new Date(a.submission.submittedAt) : new Date(0);
        const timeB = b.submission.submittedAt ? new Date(b.submission.submittedAt) : new Date(0);
        return timeA - timeB;
      });
    
    if (correctSubmissions.length > 0) {
      fastestPlayerId = correctSubmissions[0].id;
    }
  }

  let list;
  if (!playerList) {
    list = <Typography>Loading...</Typography>;
  } else {
    list = playerList.map((player, idx) => (
      <PlayerLeaderboardRow
        player={player}
        key={`player-${player.id}`}
        index={idx}
        highlighted={currentPlayer && player.id === currentPlayer.id}
        submission={player.submission}
        isResultsMode={isResultsMode}
        isWageringMode={isWageringMode}
        fastestPlayerId={fastestPlayerId}
        questionStartTime={currentQuestion?.startTime}
      />
    ));
  }

  return (
    <Root>
      <StyledContainer maxWidth="sm">
        <HeaderText variant="h5">Leaderboard</HeaderText>
        <List>{list}</List>
      </StyledContainer>
    </Root>
  );
};

export default PlayerLeaderboard;
